import type { Game, Item, Tile } from "..";
import type { Position } from "../../data/types";
import { calculateHalfBallDamage } from "../../lib/algo";
import { System, type EventHandlers } from "../../lib/core";

/** Ticks between shade flow steps. Parity rotates 0..3 (right/down/left/up). */
const SHADE_TICK_INTERVAL = 15;

const FLOW_DIRECTIONS: { dx: number; dy: number }[] = [
  { dx: 1, dy: 0 },
  { dx: 0, dy: 1 },
  { dx: -1, dy: 0 },
  { dx: 0, dy: -1 },
];

export type ExplodeEffect = {
  item: Item;
  affected: { tile: Tile; damage: number; clears: boolean }[];
};

export type ShadeEvents = {
  contact: { item: Item; tile: Tile };
};

export class ShadeSystem
  extends System<Game>()<ShadeEvents>(["contact"])
  implements EventHandlers<ShadeEvents>
{
  contact({ item, tile: _tile }: ShadeEvents["contact"]) {
    const game = this.game;
    if (!item.position) return;
    const centerTile = game.grid.tryAt(item.position);
    if (!centerTile) return;
    const effect = this.previewExplosion(item, centerTile);
    if (!effect) return;
    this.applyExplosion(effect);
  }

  /**
   * Per-tick shade work: viscous flow on the SHADE_TICK_INTERVAL beat,
   * then a contact scan for items sitting in shade (the async path —
   * synchronous explosions on drop/tap go through `contact` directly).
   */
  update(tick: number): void {
    if (tick % SHADE_TICK_INTERVAL === 0) {
      const parity = Math.floor(tick / SHADE_TICK_INTERVAL) % FLOW_DIRECTIONS.length;
      this.flow(parity);
    }
    this.scanContact();
  }

  /**
   * Paint distance-based shade onto the board around `center`.
   * - Tiles at chebyshev distance ≥ rimDistance receive `rimShade + (dist - rimDistance) * rimStep`.
   * - Tiles at distance ≥ bufferDistance and < rimDistance receive `bufferShade` (optional).
   * - Other tiles are left untouched.
   *
   * Savefile-independent primitive: callers (init.ts) supply the level layout.
   */
  applyDistanceShadeMap(opts: {
    center: Position;
    rimDistance: number;
    rimShade: number;
    rimStep: number;
    bufferDistance?: number;
    bufferShade?: number;
  }): void {
    const { center, rimDistance, rimShade, rimStep, bufferDistance, bufferShade } = opts;
    for (const tile of this.game.state.tiles.values()) {
      const dx = Math.abs(tile.position.x - center.x);
      const dy = Math.abs(tile.position.y - center.y);
      const dist = Math.max(dx, dy);
      if (dist >= rimDistance) {
        tile.shade = rimShade + (dist - rimDistance) * rimStep;
      } else if (
        bufferDistance !== undefined &&
        bufferShade !== undefined &&
        dist >= bufferDistance
      ) {
        tile.shade = bufferShade;
      }
    }
  }

  /**
   * Viscous shade diffusion. Each call pushes shade in one of four cardinal
   * directions (rotating by parity), but only between tiles whose shade ≥ 100
   * (cosmetic threshold — keeps the safe centre area stable). Items with
   * `spawnShade` add fresh shade to their tile before diffusion runs.
   */
  flow(parity: number): void {
    const game = this.game;
    const direction = FLOW_DIRECTIONS[parity];
    if (!direction) return;

    for (const item of game.state.items.values()) {
      const spawnShade = item.data.shade?.spawnShade;
      if (!spawnShade || !item.position || item.animation) continue;
      const tile = game.grid.tryAt(item.position);
      if (!tile) continue;
      if (spawnShade.enabledInShade) {
        const minShade = spawnShade.minShade ?? 0;
        if (tile.shade < minShade) continue;
      }
      tile.shade += Math.ceil(spawnShade.amount);
    }

    const shadeChanges = new Map<Tile, number>();
    for (const tile of game.state.tiles.values()) {
      if (tile.shade < 100) continue;
      const adj = game.grid.tryAt({
        x: tile.position.x + direction.dx,
        y: tile.position.y + direction.dy,
      });
      if (!adj) continue;
      const diff = tile.shade - adj.shade;
      const amount = Math.floor(diff * 0.01);
      if (amount < 1) continue;
      // Viscous: gate on the *post-flow* diff. We refuse the move unless the
      // remaining gradient is still ≥ 100, so a single 100/0 pair never
      // smears into 99/1 (its post-flow diff would only be 98).
      if (diff - 2 * amount < 100) continue;
      shadeChanges.set(tile, (shadeChanges.get(tile) ?? 0) - amount);
      shadeChanges.set(adj, (shadeChanges.get(adj) ?? 0) + amount);
    }
    for (const [tile, change] of shadeChanges) {
      tile.shade = Math.max(0, tile.shade + change);
    }
  }

  /**
   * Scan all items for `explodeOnShade` instances sitting on shaded tiles
   * (the async path: shade flowed onto an existing item). Synchronous
   * explosions from drop/tap go through `contact` directly.
   */
  scanContact(): void {
    const game = this.game;
    for (const item of game.state.items.values()) {
      if (!item.position || item.animation) continue;
      if (!item.data.shade?.explodeOnShade) continue;
      const tile = game.grid.tryAt(item.position);
      if (!tile || tile.shade <= 0) continue;
      this.contact({ item, tile });
    }
  }

  /** Compute the explosion an item would cause at `atTile`. Null if item can't explode. */
  previewExplosion(item: Item, atTile: Tile): ExplodeEffect | null {
    const explode = item.data.shade?.explodeOnShade;
    if (!explode) return null;

    const { tiles } = this.game.grid.scan({
      mode: "nearest",
      from: atTile,
      valid: () => true,
      count: null,
      range: explode.radius,
    });
    const allTiles = Array.from(new Set([atTile, ...tiles]));

    const damages = calculateHalfBallDamage(
      allTiles.map((t) => ({ position: t.position, shade: t.shade })),
      atTile.position,
      explode.power,
      explode.radius,
    );

    const affected = allTiles.map((tile, i) => {
      const damage = damages[i] ?? 0;
      return { tile, damage, clears: damage > 0 && damage >= tile.shade };
    });
    return { item, affected };
  }

  /** Apply an explosion effect: remove the exploding item and reduce shade on affected tiles. */
  applyExplosion(effect: ExplodeEffect) {
    const game = this.game;
    const { item, affected } = effect;

    game.state.items.delete(item.uid);
    if (item.position) {
      const sourceTile = game.grid.tryAt(item.position);
      if (sourceTile?.item === item) sourceTile.item = null;
    }

    for (const a of affected) {
      if (a.damage > 0) {
        a.tile.shade = Math.max(0, a.tile.shade - a.damage);
      }
    }
  }
}
